import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import type { DailyNutritionSummary } from "@/hooks/useNutritionLogs";

interface WeeklyNutritionChartProps {
  data: DailyNutritionSummary[];
  calorieTarget: number;
  className?: string;
}

export function WeeklyNutritionChart({
  data,
  calorieTarget,
  className,
}: WeeklyNutritionChartProps) {
  const chartData = useMemo(() => {
    return data.map((day) => ({
      date: day.date,
      dayLabel: format(parseISO(day.date), 'EEE'),
      calories: day.totalCalories,
      target: calorieTarget,
      isToday: day.date === format(new Date(), 'yyyy-MM-dd'),
    }));
  }, [data, calorieTarget]);

  const avgCalories = useMemo(() => {
    const daysWithData = chartData.filter(d => d.calories > 0);
    if (daysWithData.length === 0) return 0;
    return Math.round(daysWithData.reduce((sum, d) => sum + d.calories, 0) / daysWithData.length);
  }, [chartData]);

  const hasData = chartData.some(d => d.calories > 0);

  if (!hasData) {
    return (
      <div className={cn("glass-card rounded-2xl p-5", className)}>
        <h3 className="font-display text-lg font-bold mb-4">Weekly Overview</h3>
        <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">
          Start logging to see your weekly trends
        </div>
      </div>
    );
  }

  return (
    <div className={cn("glass-card rounded-2xl p-5", className)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-bold">Weekly Overview</h3>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Avg</p>
          <p className={cn(
            "text-lg font-bold",
            avgCalories > calorieTarget ? "text-red-500" : "text-primary"
          )}>
            {avgCalories.toLocaleString()} cal
          </p>
        </div>
      </div>

      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
            <XAxis 
              dataKey="dayLabel" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
              tickFormatter={(value) => value > 0 ? `${value / 1000}k` : '0'}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-popover border border-border rounded-lg p-2 shadow-lg">
                      <p className="text-xs text-muted-foreground">
                        {format(parseISO(data.date), 'MMM d')}
                      </p>
                      <p className="text-sm font-bold">
                        {data.calories.toLocaleString()} cal
                      </p>
                      {calorieTarget > 0 && (
                        <p className={cn(
                          "text-xs",
                          data.calories > calorieTarget ? "text-red-500" : "text-green-500"
                        )}>
                          {data.calories > calorieTarget 
                            ? `+${data.calories - calorieTarget} over` 
                            : `${calorieTarget - data.calories} under`}
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            {calorieTarget > 0 && (
              <ReferenceLine 
                y={calorieTarget} 
                stroke="hsl(var(--primary))" 
                strokeDasharray="5 5"
                strokeWidth={2}
              />
            )}
            <Bar 
              dataKey="calories" 
              radius={[4, 4, 0, 0]}
              fill="hsl(var(--primary))"
              className="opacity-80"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {calorieTarget > 0 && (
        <div className="flex items-center justify-center gap-4 mt-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 bg-primary rounded opacity-80" />
            <span>Calories</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 bg-primary rounded border-dashed" style={{ borderStyle: 'dashed' }} />
            <span>Target ({calorieTarget})</span>
          </div>
        </div>
      )}
    </div>
  );
}
